import {
  ContentModule,
  ModuleProperties,
  useParsedText,
  useShowInstructions,
} from '@backstage-components/base';
import {useSubscription} from 'observable-hooks';
import {cx, css} from '@emotion/css';
import {FC, useCallback, useEffect} from 'react';
import {reactName, SchemaType, ComponentDefinition} from './EmbedDefinition';

export interface IEmbedProps extends ModuleProperties, SchemaType {}

export type EmbedComponentDefinition = ContentModule<
  typeof reactName,
  IEmbedProps & SchemaType
>;

export const EmbedComponent: FC<EmbedComponentDefinition> = (definition) => {
  const styleClassName = css`
    ${definition.style}
    ${definition.props.styleAttr}
  `;
  const {props, id} = definition;
  const {allowScripts = false, content, targetOrigin = '*'} = props;

  const isAdminScope = definition.config.scope === 'admin';

  const {observable, broadcast} = useShowInstructions(
    ComponentDefinition.instructions,
    definition
  );

  const iframeElement = useCallback(() => {
    return document.querySelector<HTMLIFrameElement>(`#${id} iframe`);
  }, [id]);

  useEffect(() => {
    // Don't bother listening for messages if we're in the layout editor
    if (isAdminScope) {
      return;
    }
    const onMessageReceived = (e: MessageEvent<unknown>): void => {
      // Ensure that the message is coming from the correct iframe
      if (e.source !== iframeElement()?.contentWindow) {
        return;
      }

      // Ensure that the message is coming from the correct origin
      if (e.origin !== targetOrigin && targetOrigin !== '*') {
        console.warn(
          `Message received from incorrect origin. ${targetOrigin} was specified, but ${e.origin} was received.`
        );
        return;
      }

      // Broadcast the instruction
      broadcast({
        type: 'Embed:onReceivedPostMessage',
        meta: {message: e.data ?? null},
      });
    };

    window.addEventListener('message', onMessageReceived);

    return () => {
      window.removeEventListener('message', onMessageReceived);
    };
  }, [broadcast, iframeElement, targetOrigin, isAdminScope]);

  const sendToIframe = (message: unknown, targetOrigin: string): void => {
    iframeElement()?.contentWindow?.postMessage(message, targetOrigin);
  };

  useSubscription(observable, (inst) => {
    if (inst.type === 'Embed:SendPostMessage') {
      sendToIframe(inst.meta.message, inst.meta.targetOrigin || '*');
    }
  });

  return (
    <div id={id} className={cx(styleClassName, definition.mid)}>
      {useParsedText(content, {allowScripts})}
    </div>
  );
};
